import { Account, AccountStatus, RotateWalletWebhookSecretResponse, UpdateAccountDto } from "@/types";
import { CustomMap } from "@/types/ICustomMap";
import api from "@/utils/api";
import { AccountTier, TemporaryAccess, AccountRevenueHistory, AccountTierHistory, TCreateAccount, AccountTierLevel } from './types';
import { ApiListResponse } from "@/types/api";

interface GetAccountsResponse extends ApiListResponse {
    accounts: Account[];
}

export const getAccounts = async (filters: CustomMap): Promise<GetAccountsResponse> => {
    const response = await api.get('/admin/accounts', { params: filters });
    return response.data.data;
}

export const getTemporaryAccess = async (accountId: string): Promise<TemporaryAccess> => {
    return (await api.post(`/accounts/${accountId}/temporary-access`)).data.data;
}

export const getAccountDetails = async (accountId: string): Promise<Account> => {
    return (await api.get(`/admin/accounts/${accountId}`)).data.data;
}

/** Get projects for an end customer account (Command Center super admin) */
export const getAccountProjects = async (accountId: string) => {
    return (await api.get(`/admin/accounts/${accountId}/projects`)).data;
};

/** Get payment methods for an account (Command Center super admin) */
export const getAccountPaymentMethods = async (accountId: string) => {
    const res = await api.get(`/admin/accounts/${accountId}/payment-methods`);
    return res.data?.data ?? res.data ?? [];
};

/** Get documents (signed proposals) for an end customer account (Command Center super admin) */
export const getAccountDocuments = async (accountId: string) => {
    return (await api.get(`/admin/accounts/${accountId}/documents`)).data ?? [];
};

export const updateAccount = async ({ id, data }: { id: string, data: Partial<UpdateAccountDto> }): Promise<Account> => {
    return (await api.patch(`/admin/accounts/${id}`, data)).data.data;
}

/**
 * Switch an account's default selling currency (Task 2.11, design D1).
 * Internal-only (SUPER_ADMIN role OR INTERNAL accountType) — enforced
 * server-side; existing quotes/orders keep their own currency snapshot.
 */
export const updateAccountCurrency = async (
    accountId: string,
    currency: string,
): Promise<{ id: string; currency: string; oldCurrency: string }> => {
    return (await api.patch(`/accounts/${accountId}/currency`, { currency })).data.data;
}

/**
 * Rotate the wallet webhook secret for an API_PARTNER account.
 *
 * SECURITY: The returned `walletWebhookSecret` is the plain-text value —
 * surface it to the user EXACTLY ONCE via the reveal modal, then drop
 * it from React state. There is no way to retrieve the value again
 * after rotation; subsequent reads only return the masked variant.
 *
 * Used by: Command Center > Account Details (super admin rotate button).
 */
export const rotateAccountWalletWebhookSecret = async (
    accountId: string
): Promise<RotateWalletWebhookSecretResponse> => {
    const response = await api.post(`/admin/accounts/${accountId}/wallet-webhook-secret/rotate`);
    return response.data.data;
};

export const getAccountTier = async (accountId: string): Promise<AccountTier> => {
    return (await api.get(`/admin/dealer-discounts/accounts/${accountId}/tier`)).data || null;
}

export const getAccountRevenueHistory = async (accountId: string): Promise<AccountRevenueHistory[]> => {
    return (await api.get(`/admin/dealer-discounts/accounts/${accountId}/revenue`)).data || null;
}

export const getAccountTierHistory = async (accountId: string): Promise<AccountTierHistory[]> => {
    return (await api.get(`/admin/dealer-discounts/accounts/${accountId}/tier/history`)).data || null;
}

// DISABLED 2026-07-02 — the check-upgrade endpoint (revenue-based tier
// automation) is commented out on the backend; tiers are manual-only now.
// export const checkAndUpgradeTier = async (accountId: string): Promise<void> => {
//     return (await api.post(`/admin/dealer-discounts/accounts/${accountId}/tier/check-upgrade`)).data || null;
// }

export const createAccount = async (data: TCreateAccount): Promise<Account> => {
    return (await api.post('/admin/accounts', data)).data.data;
};

export const updateAccountTier = async ({ id, tierLevel }: { id: string, tierLevel: AccountTierLevel }): Promise<AccountTier> => {
    return (await api.patch(`/admin/dealer-discounts/accounts/${id}/tier`, { tierLevel })).data.data;
}

export const bulkUpdateAccountTiers = async ({
    accountIds,
    tierLevel
}: { accountIds: string[]; tierLevel: AccountTierLevel }) => {
    return (await api.post('/admin/dealer-discounts/accounts/bulk-tier-update', { accountIds, tierLevel })).data;
}

export const bulkUpdateAccountStatus = async ({
    accountIds,
    status
}: { accountIds: string[]; status: AccountStatus }) => {
    return (await api.post('/admin/accounts/bulk-status-update', { accountIds, status })).data;
}

export const getAllCommissionTiers = async () => {
    return (await api.get('/api/commission-tiers')).data || [];
};

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

export interface OnboardingCompanyInput {
    workspace_name: string;
    name: string;
    workspace_photo_path?: string;
    workspace_logo_path?: string;
    workspace_primary_color?: string;
    customer_type?: string;
    enable_face_recognition?: boolean;
    qb_account_id?: string | null;
    /** Salesforce Opportunity / Account URL */
    salesforce_link?: string;
    /** Project tracker URL (Jira / Asana / Linear / etc.) */
    project_tracker_link?: string;
}

export interface OnboardingAdminInput {
    first_name: string;
    last_name: string;
    email: string;
    send_invite?: boolean;
}

export interface OnboardingSiteInput {
    name: string;
    address: string;
    country: string;
    phone_number?: string;
    timezone?: string | null;
}

export interface OnboardingAccessPointInput {
    short_name: string;
    description: string;
}

export interface OnboardingPayload {
    company: OnboardingCompanyInput;
    admin: OnboardingAdminInput;
    site: OnboardingSiteInput;
    access_points: OnboardingAccessPointInput[];
    groups?: string[];
}

/**
 * Provision a Swiftlane workspace for an end-customer account by calling
 * the internal onboarding API through the partner-portal backend proxy.
 */
export const onboardAccount = async (
    accountId: string,
    payload: OnboardingPayload,
) => {
    const res = await api.post(`/admin/accounts/${accountId}/onboard`, payload);
    return res.data;
};